import { DesignOption } from '#base/Designer/index.js';
import { formatCurrency } from '@c2s-npm/plumlib';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

const exists = (e) => !!e;

export const getGroupsContainingAsset = (groups, assetId) => {
  const assetGroups = groups.filter((g) => g.assets.includes(assetId));

  const getParents = (g) => {
    const parents = groups.filter(({ id }) => g.parents.includes(id));
    if (parents.length > 0) {
      return parents.concat(parents.flatMap(getParents));
    } else {
      return parents;
    }
  };

  return assetGroups.concat(assetGroups.flatMap(getParents));
};

export const useGrouping = ({ groups, assets, selected, elements, assetGroupFilter, graphicTypeFilter }) => {
  const [selectedGroup, _selectGroup] = useState(null);
  const isTablet = useMediaQuery({ maxWidth: 1023 });

  const selectGroup = useCallback(
    (e) => {
      const group = typeof e === 'object' ? e : groups.find((g) => g.id === e);
      if (group !== selectedGroup) {
        _selectGroup(group || 'root');
        if (isTablet) window.scrollTo(window.scrollX, 0);
      }
    },
    [groups, selectedGroup, isTablet]
  );

  const [subGroups, subAssets] = useMemo(() => {
    const isRoot = !selectedGroup || selectedGroup === 'root';

    if (selected) {
      const includes = groups.filter((g) => g.assets.includes(selected));
      if (includes.length > 0 && !selectedGroup && !includes.includes(selectedGroup)) {
        selectGroup(includes[0]);
      }
    }

    if (isRoot && groups && groups.length > 0) {
      const groupFilter = typeof assetGroupFilter === 'function' ? assetGroupFilter() : assetGroupFilter;
      const finalFilter = Array.isArray(groupFilter) ? groupFilter : [groupFilter];

      let filteredGroups = assetGroupFilter
        ? groups.filter((g) => finalFilter.includes(g.id) && g.hasAssets).filter(exists)
        : groups.filter((g) => !g.parents?.length && g.hasAssets).filter(exists);

      if (graphicTypeFilter) {
        filteredGroups = filteredGroups.filter(groupsGraphicTypeFilter(graphicTypeFilter));
      }

      return [filteredGroups, []];
    } else if (selectedGroup) {
      let filteredGroups = selectedGroup.groups
        .map((id) => groups.find((a) => a.id === id && a.hasAssets))
        .filter(exists);

      let filteredAssets = selectedGroup.assets.map((id) => assets.find((a) => a.key === id)).filter(exists);

      if (graphicTypeFilter) {
        filteredGroups = filteredGroups.filter(groupsGraphicTypeFilter(graphicTypeFilter));
        filteredAssets = filteredAssets.filter(assetsGraphicTypeFilter(graphicTypeFilter));
      }

      return [filteredGroups, filteredAssets];
    } else {
      let filteredAssets = assets;

      if (graphicTypeFilter) {
        filteredAssets = filteredAssets.filter(assetsGraphicTypeFilter(graphicTypeFilter));
      }

      return [[], filteredAssets];
    }
  }, [assets, groups, selectedGroup, selected, assetGroupFilter]);

  useEffect(() => {
    if (subGroups.length === 1) {
      selectGroup(subGroups[0]);
    }
  }, [subGroups]);

  const hasPremium = subAssets.some((a) => !!a?.fee);
  // const hasSelected = selectedGroup?.assets?.includes?.(selected);
  const hasOtherElements = elements?.size > (selected ? 1 : 0);

  return [subGroups, subAssets, selectedGroup, selectGroup, hasPremium, hasOtherElements];
};

export const AssetGroup = ({ groups, activeGroups, selectGroup, hasElements, hasPremium }) => {
  const [shownGroups, setShownGroups] = useState(groups);

  useEffect(() => {
    const existingGroups = groups.filter(
      (g) =>
        g.assets.length > 0 ||
        (g.groups.length > 0 &&
          g.groups.some((group) => {
            return activeGroups.findIndex((activeGroup) => activeGroup.id === group) >= 0;
          }))
    );
    setShownGroups(existingGroups);
  }, [groups]);

  return (
    <>
      {shownGroups.length > 0 &&
        shownGroups.map((group) => (
          <DesignOption
            key={group.groupName}
            iconUrl={group.previewUrl}
            text={group.groupName}
            subtext={
              !!group?.fee && (
                <>
                  <span
                    className={classNames('t-block t-text-2xs t-leading-[12px]', {
                      't-font-bold t-text-onsale': !!group?.usualRate,
                      't-text-brand-black': !group?.usualRate,
                    })}>
                    {formatCurrency(group?.fee)}
                  </span>
                  {/* {!!group?.usualRate && (
                    <span className='t-block t-scale-90 t-text-2xs t-leading-[12px] t-text-muted t-line-through'>
                      {currency(group.usualRate)}
                    </span>
                  )} */}
                </>
              )
            }
            onClick={() => selectGroup(group)}
            disabled={!!hasPremium && hasElements && (!activeGroups || activeGroups.includes(group.id))}
            mode='grid'
          />
        ))}
    </>
  );
};

export const groupsGraphicTypeFilter = (graphicType) => (g) =>
  !g.graphicType || g.graphicType.length === 0 || g.graphicType.includes(graphicType);

export const assetsGraphicTypeFilter = (graphicType) => (a) =>
  !a?.attributes?.graphicType || a?.attributes?.graphicType?.includes?.(graphicType);
